import posthog, { type CaptureResult, type PostHog } from 'posthog-js';
import { FONT_PROBE_EVENT, scheduleFontProbe } from './font-probe';
import { redactUrl } from './redact-url';

// Contract with public/legacy-shims.js, which runs before any app chunk
// (synchronous script at the top of <body> in app/layout.tsx) and queues
// window errors that fire before posthog-js has initialised.
declare global {
  interface Window {
    __t3kEarlyErrors?: {
      error: unknown;
      message: string;
      kind: string;
      stack: string | null;
    }[];
    __t3kEarlyErrorsUninstall?: () => void;
    /** Set here; read by legacy-shims.js to skip its raw-fetch boot-failure fallback. */
    __t3kPosthogReady?: boolean;
  }
}

const EXTENSION_URL = /^(?:safari-web|safari|chrome|moz)-extension:\/\//i;

// Same-origin reverse proxy prefix (rewrites in next.config.mjs). Opaque on
// purpose: PostHog's default `/ingest` is on ad-block filter lists. Keep in
// sync with next.config.mjs, middleware.ts and public/legacy-shims.js.
const PROXY_PREFIX = '/t3k-da';

// Ad blockers match PostHog's *sub-paths* too, independent of host or prefix:
// uBlock Origin ships `/e/*^ip=*^compression=` and `/e/*^ip=*^beacon=`, and
// EasyPrivacy/AdGuard list the lazy-loaded `/posthog-recorder.js` and
// `/dead-clicks-autocapture.js` by filename. `rewriteRequestPath` lets us
// rename every segment the SDK requests; next.config.mjs maps each alias back
// to the canonical PostHog path. The two tables below and the rewrites there
// must change together.
const SEGMENT_ALIASES: Record<string, string> = {
  e: 'ev', // event capture (pre remote-config)
  i: 'iv', // event capture once remote config points analytics at /i/v0/e/
  s: 'rp', // session replay
  flags: 'fl', // feature flags + remote config decide
  static: 'st', // lazy-loaded extensions (recorder, surveys, web-vitals…)
  array: 'ar', // remote config bundle
};
const FILE_ALIASES: Record<string, string> = {
  'posthog-recorder.js': 'rec.js',
  'dead-clicks-autocapture.js': 'dc.js',
};

// Receives the fully resolved URL (relative api_host already applied), so
// pathnames arrive as `/t3k-da/e/`, `/t3k-da/static/1.2.3/posthog-recorder.js`, …
// Anything not in the tables passes through untouched and is served by the
// catch-all rewrite.
function rewriteRequestPath(url: URL): URL {
  const base = `${PROXY_PREFIX}/`;
  if (!url.pathname.startsWith(base)) return url;
  const [head, ...tail] = url.pathname.slice(base.length).split('/');
  const alias = SEGMENT_ALIASES[head];
  if (!alias) return url;
  const rest = tail.map((seg) => FILE_ALIASES[seg] ?? seg).join('/');
  url.pathname = `${base}${alias}/${rest}`;
  return url;
}

// Error-tracking hygiene Sentry gave us for free: drop $exception events that
// clearly originate from a browser extension rather than our code — i.e. the
// stack has frames with filenames and every one of them is an extension:// URL.
// (Seen live: a Safari extension throwing `e.useCache` TypeErrors on /search.)
// Deliberately conservative: events with no usable frames, or with any
// first-party frame, always pass through.
function dropExtensionErrors(event: CaptureResult | null): CaptureResult | null {
  if (event?.event !== '$exception') return event;
  const exceptions = (event.properties?.['$exception_list'] ?? []) as {
    stacktrace?: { frames?: { filename?: string }[] };
  }[];
  const filenames = exceptions
    .flatMap((e) => e.stacktrace?.frames ?? [])
    .map((f) => f.filename)
    .filter((f): f is string => Boolean(f));
  if (filenames.length > 0 && filenames.every((f) => EXTENSION_URL.test(f))) {
    return null;
  }
  return event;
}

// posthog-js stamps window.location on every event, so the pageview fix alone isn't enough.
export function redactEventUrls(event: CaptureResult | null): CaptureResult | null {
  const props = event?.properties;
  if (!props) return event;
  for (const key of ['$current_url', '$referrer', '$initial_current_url'] as const) {
    const value = props[key];
    if (typeof value === 'string') props[key] = redactUrl(value);
  }
  return event;
}

// The PostHog singleton lives outside React, so who initialises it matters.
// app/providers.tsx does it for the app tree, but app/global-error.tsx renders
// *instead of* the root layout — the provider never runs there, and an
// exception thrown during hydration would go uncaptured on the one code path
// that most needs to report. Init is idempotent; call this from both.
export function initPostHog(): PostHog {
  if (!posthog.__loaded) {
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
      api_host: PROXY_PREFIX,
      ui_host: 'https://us.posthog.com',
      rewriteRequestPath,
      capture_pageview: false,
      capture_pageleave: true,
      // Error tracking: autocapture unhandled exceptions and unhandled promise
      // rejections as $exception events from init onwards, tied to the current
      // person + session. Set in code (not just the PostHog project toggle) so
      // capture starts immediately rather than after the remote-config fetch —
      // early-load errors are exactly the ones old webviews hit.
      capture_exceptions: true,
      before_send: [dropExtensionErrors, redactEventUrls],
    });
    drainEarlyErrors();
    scheduleFontProbe((props) => posthog.capture(FONT_PROBE_EVENT, props));
  }
  return posthog;
}

// Report errors that fired before init (queued by legacy-shims.js), then hand
// exception capture over to posthog-js's own autocapture.
function drainEarlyErrors(): void {
  if (typeof window === 'undefined') return;
  window.__t3kPosthogReady = true;
  window.__t3kEarlyErrorsUninstall?.();
  const queued = window.__t3kEarlyErrors ?? [];
  window.__t3kEarlyErrors = [];
  for (const item of queued) {
    // A real Error object carries its own stack; for message-only events
    // (e.g. cross-origin or SyntaxError reports) attach the raw stack string.
    posthog.captureException(item.error ?? new Error(item.message), {
      boundary: 'pre-init',
      early_kind: item.kind,
      early_stack: item.error ? undefined : item.stack,
    });
  }
}
